import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { Link, type LinkProps } from "react-router";
import { OFFLINE_WRITE_REASON, useCanWrite } from "../lib/connectivity";
import { Spinner } from "./Spinner";

export type Emphasis = "solid" | "outline" | "ghost";
export type Intent = "brand" | "neutral" | "success" | "warning" | "info" | "danger";
export type Size = "sm" | "md" | "lg";

export interface ButtonStyleProps {
  emphasis?: Emphasis;
  intent?: Intent;
  size?: Size;
  block?: boolean;
  icon?: ReactNode;
  iconEnd?: ReactNode;
}

export function buttonClass({ emphasis = "outline", intent = "neutral", size = "md", block }: ButtonStyleProps, extra?: string) {
  return ["btn", `btn--${emphasis}`, `btn--${intent}`, `btn--${size}`, block ? "btn--block" : "", extra ?? ""].filter(Boolean).join(" ");
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">, ButtonStyleProps {
  children?: ReactNode;
  busy?: boolean;
  /** This action changes server data: disable it while offline (explained by the offline banner). */
  write?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({ emphasis, intent, size, block, icon, iconEnd, busy, write, className, children, type = "button", disabled, onClick, ref, ...rest }: ButtonProps) {
  const canWrite = useCanWrite();
  const blockedOffline = !!write && !canWrite;
  const isDisabled = disabled || blockedOffline;
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClass({ emphasis, intent, size, block }, className)}
      disabled={isDisabled}
      aria-busy={busy || undefined}
      aria-describedby={blockedOffline ? "offline-banner" : rest["aria-describedby"]}
      title={blockedOffline ? OFFLINE_WRITE_REASON : undefined}
      onClick={(event) => {
        if (busy || isDisabled) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      <span className="btn__content" aria-hidden={busy || undefined}>
        {icon ? <span className="btn__icon">{icon}</span> : null}
        {children !== undefined && children !== null ? <span className="btn__label">{children}</span> : null}
        {iconEnd ? <span className="btn__icon">{iconEnd}</span> : null}
      </span>
      {busy ? (
        <span className="btn__busy">
          <Spinner size="sm" label="Working" />
        </span>
      ) : null}
    </button>
  );
}

export interface ButtonLinkProps extends Omit<LinkProps, "children">, ButtonStyleProps {
  children?: ReactNode;
}

/** Navigation styled as a button (a real link). */
export function ButtonLink({ emphasis, intent, size, block, icon, iconEnd, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClass({ emphasis, intent, size, block }, className)} {...rest}>
      <span className="btn__content">
        {icon ? <span className="btn__icon">{icon}</span> : null}
        {children ? <span className="btn__label">{children}</span> : null}
        {iconEnd ? <span className="btn__icon">{iconEnd}</span> : null}
      </span>
    </Link>
  );
}

/** External link (http/https only) styled as a button. */
export function ButtonAnchor({ emphasis, intent, size, block, icon, iconEnd, className, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & ButtonStyleProps) {
  return (
    <a className={buttonClass({ emphasis, intent, size, block }, className)} {...rest}>
      <span className="btn__content">
        {icon ? <span className="btn__icon">{icon}</span> : null}
        {children ? <span className="btn__label">{children}</span> : null}
        {iconEnd ? <span className="btn__icon">{iconEnd}</span> : null}
      </span>
    </a>
  );
}
